import { normalizeOptionLabel } from '@sportninja/common/utils/optionLabel'
import { QuestionFormDraft } from '../pages/Schedule/RegistrationSetup/forms/CustomForm/types'
import {
  MAX_OPTIONS,
  MAX_OPTION_LABEL_LENGTH,
} from '../pages/Schedule/RegistrationSetup/forms/CustomForm/optionListText'

/**
 * Validates a question form draft to determine if all required fields are filled
 *
 * The option bounds live HERE rather than in the paste parser on purpose: a
 * parser that returned only the first 500 of a 501-line paste would save 500
 * options and lose one, with nothing on screen for the user to clear. The parser
 * keeps everything; this blocks the save, and OptionEditor renders the reason.
 *
 * @param formData - The question form draft to validate
 * @returns boolean - true if the form is valid and save button should be enabled
 */
export const isQuestionFormValid = (formData: QuestionFormDraft): boolean => {
  // All question types require a non-empty label
  if (!formData.label.trim()) {
    return false
  }

  // Choice questions (dropdown) require at least one option with a non-empty label
  if (formData.type === 'dropdown') {
    const validOptions = formData.options.filter(({ label }) => label.trim())

    if (validOptions.length === 0) {
      return false
    }

    // Count real options, not the blank editor rows sitting between them.
    if (validOptions.length > MAX_OPTIONS) {
      return false
    }

    // Measured on the CANONICAL label, because that is what
    // convertOptionsToParameters emits — surrounding and repeated inner
    // whitespace never reaches the varchar(191) column, so neither may block a
    // save it would not have affected.
    if (
      formData.options.some(
        ({ label }) =>
          normalizeOptionLabel(label).length > MAX_OPTION_LABEL_LENGTH
      )
    ) {
      return false
    }
  }

  // All other question types only require the label to be filled
  // Number type: min/max are optional
  // Text-like types: max_char is optional
  // Other types: no additional required fields

  return true
}

/**
 * Returns CSS styles for disabled button state
 *
 * @param isDisabled - Whether the button should be disabled
 * @returns CSS object with appropriate styles
 */
export const getDisabledButtonStyles = (isDisabled: boolean) => {
  return {
    opacity: isDisabled ? 0.5 : 1,
    pointerEvents: isDisabled ? 'none' : 'auto',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
  } as const
}
